import { GridCellProps } from '@progress/kendo-react-grid';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import React, { useState } from 'react';
import { ItemValueType } from '../../../Models/PerformanceItem';
import { IPerformanceReportItemValue } from '../../../Models/PerformanceReportItemValue';
import { Tooltip } from '@mui/material';

export default function EditorCell(props: GridCellProps) {
  // Display formatting – whole numbers show without ".00"
  const formatValue = (value: any): string => {
    if (props.dataItem.valueType === ItemValueType.Text) {
      return value || '';
    }
    const numValue = Number(value);
    if (!Number.isFinite(numValue)) return '0';

    const rounded = Math.round(numValue * 100) / 100;
    return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2);
  };

  const [value, setValue] = useState(formatValue(props.dataItem.value));

  const changeHandler = (event: InputChangeEvent, isText: boolean) => {
    if (isText) {
      props.onChange?.({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: event.syntheticEvent,
        value: event.target.value
      });
    } else {
      const numValue = Number(event.value);
      if (Number.isFinite(numValue)) {
        const roundedValue = Math.round(numValue * 100) / 100;
        props.onChange?.({
          dataIndex: 0,
          dataItem: props.dataItem,
          field: props.field,
          syntheticEvent: event.syntheticEvent,
          value: roundedValue
        });
      }
    }
  };

  const dataItem = props.dataItem as IPerformanceReportItemValue;

  // ---- normalization helpers ----
  // Zero-like detector: "0", "0.0", "+0.00", "-0", etc
  const isZeroLike = (s: string) => /^[-+]?0*(?:\.0+)?$/.test(s);

  // Parse a value into a 2-decimal normalized string; return null if unusable.
  const toNorm = (
    v: unknown,
    { zeroMeansMissing = false }: { zeroMeansMissing?: boolean } = {}
  ): string | null => {
    if (v == null) return null;
    const raw = String(v).trim();

    // If there are no digits at all, it's not a number.
    if (!/\d/.test(raw)) return null;

    // Strip $, commas, spaces and any non numeric/exp chars
    const cleaned = raw.replace(/[^0-9.+\-Ee]/g, '');

    // Treat zero-like as "missing" if requested (used for Per DMS)
    if (zeroMeansMissing && isZeroLike(cleaned)) return null;

    const n = Number(cleaned);
    if (!Number.isFinite(n)) return null;

    // Round to 2-dec and normalize as a fixed string so 241 === 241.00
    const rounded = Math.round(n * 100) / 100;
    return rounded.toFixed(2);
  };

  // Per DMS (source) – many feeds encode "no value" as 0; treat zero-like as missing here.
  const normSource = toNorm(dataItem.sourceValue, { zeroMeansMissing: true });
  // Current value – 0 is a valid entry here.
  const normCurrent = toNorm(dataItem.value, { zeroMeansMissing: false });

  const isDifferentFromDMS =
    normSource !== null && normCurrent !== null && normSource !== normCurrent;

  return (
    <td style={{ display: 'flex', alignItems: 'center' }}>
      <Input
        valid={true}
        type={props.dataItem.valueType === ItemValueType.Text ? 'text' : 'number'}
        value={value}
        readOnly={!props.dataItem.inEdit}
        onChange={(event: InputChangeEvent) => {
          // Store raw value during editing - don't format yet!
          const newValue = event.value ?? '';
          setValue(newValue);
          changeHandler(event, props.dataItem.valueType === ItemValueType.Text);
        }}
        onBlur={() => {
          // Format only when leaving the field
          if (props.dataItem.valueType !== ItemValueType.Text) {
            setValue(formatValue(value));
          }
        }}
      />
      {isDifferentFromDMS && (
        <Tooltip title="The values for Per DMS are different from what you entered. Ignore this if that is what you intended.">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'red',
              background: 'white',
              border: 'solid 2px red',
              cursor: 'pointer',
              borderRadius: '50%',
              width: '25px',
              height: '20px',
              marginLeft: '5px'
            }}
          >
            <span style={{ fontWeight: 'bold' }}>?</span>
          </div>
        </Tooltip>
      )}
    </td>
  );
}